import { booleanAttribute, Component, Input, OnInit } from '@angular/core';
import { JobAndUnitRequirementsDto, RequirementDto } from '../../models/job-unit-requirements.model';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import {
    selectJobQualifications,
    selectJobQualificationsLoading
} from '../job-qualifications/selectors/job-qualifications.selectors';
import { loadQualifications } from '../job-qualifications/actions/job-qualifications.actions';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { JobQualificationsMatchTypeEnum } from '../job-qualifications/enums/job-qualifications-match-type.enum';
import { LDFeatureManager } from '../../services/ld-feature-manager';
import { FeatureFlag } from '../../models/enums/feature-flag';

@UntilDestroy()
@Component({
    selector: 'nova-job-unit-requirements',
    templateUrl: './job-unit-requirements.component.html',
    styleUrls: ['./job-unit-requirements.component.scss']
})
export class JobUnitRequirementsComponent implements OnInit {
    @Input({ transform: booleanAttribute })
    showUnitDetails: boolean;

    @Input({ transform: booleanAttribute })
    showJobRequirementsAndQualifications: boolean;

    @Input()
    data: JobAndUnitRequirementsDto;

    @Input() useClass: string;

    @Input() jobId: number;

    isLoading$: Observable<boolean>;

    currentToggleText = 'current';
    compareToggleText = 'compare';

    udCompareSelection$ = new BehaviorSubject<'current' | 'ud-compare'>('current'); // remove with allowJobAndUDWithCTRequirementsComparisonEnabled FF cleanup
    compareSelection$ = new BehaviorSubject<'current' | 'compare'>('current');
    matchType = JobQualificationsMatchTypeEnum;
    canReloadJobQualifications = false;

    allowJobAndUDWithCTRequirementsComparisonEnabled = false;
    allowJobAndUDWithCTRequirementsCombinationEnabled = false;
    allowJobWithCTRequirementsModificationEnabled = false;

    constructor(
        private readonly store: Store,
        private readonly _ldFeatureManager: LDFeatureManager
    ) {}

    ngOnInit(): void {
        // Remove this block entirely when cleaning FF. It was added for the edge case where the FF status changes while the user is working with job details page.
        combineLatest([
            this._ldFeatureManager.isEnabled(FeatureFlag.AllowJobAndUDWithCTRequirementsCombination),
            this._ldFeatureManager.isEnabled(FeatureFlag.AllowJobWithCTRequirementsModification)
        ])
            .pipe(untilDestroyed(this))
            .subscribe(([allowJobAndUDWithCTRequirementsCombinationFlag]) => {
                this.allowJobAndUDWithCTRequirementsCombinationEnabled = allowJobAndUDWithCTRequirementsCombinationFlag;
                if (!this.canReloadJobQualifications) {
                    this.canReloadJobQualifications = true;
                } else {
                    this.store.dispatch(loadQualifications({ jobId: this.jobId }));
                }
            });

        this._ldFeatureManager
            .isEnabled(FeatureFlag.AllowJobAndUDWithCTRequirementsComparison)
            .pipe(untilDestroyed(this))
            .subscribe((flagIsEnabled) => {
                this.allowJobAndUDWithCTRequirementsComparisonEnabled = flagIsEnabled;
            });

        this._ldFeatureManager
            .isEnabled(FeatureFlag.AllowJobAndUDWithCTRequirementsComparison)
            .pipe(untilDestroyed(this))
            .subscribe((flagIsEnabled) => {
                this.allowJobWithCTRequirementsModificationEnabled = flagIsEnabled;
            });

        this.data = null;
        this.isLoading$ = this.store.select(selectJobQualificationsLoading);

        this.store.dispatch(loadQualifications({ jobId: this.jobId }));

        this.store
            .select(selectJobQualifications)
            .pipe(untilDestroyed(this))
            .subscribe((data) => {
                this.data = data;
            });
    }

    get shouldDisplayComparison() {
        return (
            (this.data?.matchType === this.matchType.UnitRequirements ||
                (this.allowJobWithCTRequirementsModificationEnabled &&
                    this.data?.matchType === this.matchType.JobTrack) ||
                (this.allowJobAndUDWithCTRequirementsCombinationEnabled &&
                    this.data?.matchType === this.matchType.UnitTrack)) &&
            this.data?.hasJobLevelEdits &&
            !this.data?.allChangesMatch
        );
    }

    get comparisonText() {
        const matchType = this.data?.matchType;
        switch (matchType) {
            case this.matchType.JobTrack:
                return 'Track Comp';
            case this.matchType.UnitTrack:
            case this.matchType.UnitRequirements:
                return 'UD Comp';
            default:
                return '';
        }
    }

    getUniqueSkillsCategories(data: RequirementDto[]) {
        const categories = data.filter((c) => !c.isNA).map((item) => item.category);
        return [...new Set(categories)];
    }

    getSkillsByCategory(data: RequirementDto[], category) {
        return data.filter((item) => item.category === category && !item.isNA);
    }

    onUdChangesToggled(toggleChange: MatButtonToggleChange) {
        this.udCompareSelection$.next(toggleChange.value);
    }

    onChangesToggled(toggleChange: MatButtonToggleChange) {
        this.compareSelection$.next(toggleChange.value);
    }
}
